import React from "react";
import { CheckCircle2, PencilLine, Circle, AlertCircle, EyeOff } from "lucide-react";

/**
 * Uniform status badge for editorial blocks.
 * States:
 *   - published : green
 *   - draft     : yellow (has draft_content)
 *   - fallback  : slate  (empty content / not initialised)
 *   - missing   : red    (expected but absent)
 */
export function BlockStatusBadge({ state, is_visible = true, size = "sm" }) {
  const config = {
    published: { label: "Pubblicato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    draft:     { label: "Bozza",       cls: "bg-yellow-50 text-yellow-800 border-yellow-300", icon: PencilLine },
    fallback:  { label: "Fallback",    cls: "bg-slate-100 text-slate-600 border-slate-200",   icon: Circle },
    missing:   { label: "Mancante",    cls: "bg-red-50 text-red-700 border-red-200",           icon: AlertCircle },
  }[state] || { label: state, cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Circle };
  const Icon = config.icon;
  const sizing = size === "sm" ? "text-[11px] px-2 py-0.5 gap-1" : "text-xs px-2.5 py-1 gap-1.5";
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center rounded-full border ${sizing} ${config.cls}`} data-testid={`block-status-${state}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
      {!is_visible && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title="Blocco nascosto dal sito pubblico">
          <EyeOff className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
}

/** Pallino inline per singolo campo. */
export function FieldStatusDot({ state }) {
  const color = {
    published: "bg-emerald-500",
    draft: "bg-yellow-400",
    fallback: "bg-slate-300",
  }[state] || "bg-slate-300";
  const title = {
    published: "Valore pubblicato",
    draft: "Valore in bozza (non ancora pubblicato)",
    fallback: "Nessun valore salvato: il sito userà il fallback dal codice",
  }[state] || "";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} title={title} />;
}

/** Format relative time in Italian (fresh vs old). */
export function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "meno di un minuto fa";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} giorni fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
